import { z } from 'zod';
/**
 * The API's one error envelope, normalised once (§4.5).
 *
 * Every error leaves the backend as `{ error, code, details? }`. Nothing
 * downstream parses a response body again.
 */
export const ErrorBody = z.object({
    error: z.string(),
    code: z.string(),
    details: z.unknown().optional(),
});
/** `VALIDATION_FAILED` carries a Zod flatten — the exact shape RHF's setError wants. */
export const FieldErrors = z.record(z.string(), z.array(z.string()));
export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, body) {
        super(body.error);
        this.name = 'ApiError';
        this.status = status;
        this.code = body.code;
        this.details = body.details;
    }
    /**
     * The field errors, when this is a validation failure.
     *
     * Returns null rather than throwing on an unexpected shape: a malformed
     * `details` should degrade to a form-level message, not replace a validation
     * error with a parse error nobody can act on.
     */
    get fieldErrors() {
        const parsed = FieldErrors.safeParse(this.details);
        return parsed.success ? parsed.data : null;
    }
}
/** A transport or parse failure — no envelope, so no `code` to switch on. */
export class NetworkError extends Error {
    cause;
    constructor(message, cause) {
        super(message);
        this.name = 'NetworkError';
        this.cause = cause;
    }
}
/**
 * The response did not match its contract.
 *
 * Distinct from NetworkError because the fix is different: this one is a
 * backend change we did not hear about, and it must be loud in development
 * rather than rendering as an empty card (§4.6).
 */
export class ContractError extends Error {
    path;
    issues;
    constructor(path, issues) {
        super(`Response from ${path} did not match its contract`);
        this.name = 'ContractError';
        this.path = path;
        this.issues = issues;
    }
}
export function isApiError(error) {
    return error instanceof ApiError;
}
/**
 * Whether this failure means the session is gone.
 *
 * Load-bearing: it is the ONLY thing that clears the tokens (§4.3). A 502, a
 * 500 or an offline phone must leave them in place, or a gateway hiccup signs
 * a member out mid-session.
 */
export function isUnauthenticated(error) {
    return isApiError(error) && (error.status === 401 || error.code === 'UNAUTHENTICATED');
}
export function isForbidden(error) {
    return isApiError(error) && (error.status === 403 || error.code === 'FORBIDDEN');
}
export function isNotFound(error) {
    return isApiError(error) && (error.status === 404 || error.code.endsWith('NOT_FOUND'));
}
/**
 * An already-registered email is never a dead end — the backend raises this
 * code specifically so the funnel can route to sign-in instead of failing a
 * form the person cannot correct (§4.5).
 */
export function isEmailAlreadyRegistered(error) {
    return isApiError(error) && error.code === 'EMAIL_ALREADY_REGISTERED';
}
/**
 * The checkout an unpaid signup should go back to, when sign-in answers
 * `SIGNUP_AWAITING_PAYMENT` — or null for any other error.
 *
 * The account exists before the payment, and a pending account cannot sign in.
 * With the right password the backend names the unpaid checkout instead of the
 * uniform refusal, so the person lands on the pay step rather than being told
 * their correct password is wrong (2 Oct 2026).
 */
export function signupAwaitingPayment(error) {
    if (!isApiError(error) || error.code !== 'SIGNUP_AWAITING_PAYMENT')
        return null;
    const details = error.details;
    return typeof details?.checkoutId === 'string' ? details.checkoutId : null;
}
//# sourceMappingURL=errors.js.map